import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  convertTableDataToCorrectFormat,
  getTableAnswerKeys,
  isCellDisabled,
  isCellReadOnly,
  parseAnswerData,
} from './Util/Helpers';
import { emptyTableWithDatetime } from './Util/HandleInitialiseTable';

// MUI
import Grid from '@mui/material/Grid';
import { styled, createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Input types
import TextInput from './Inputs/TextInput';
import DatetimeInput from './Inputs/DatetimeInput';
import NumberInput from './Inputs/NumberInput';
import DropdownInput from './Inputs/DropdownInput';

const theme = createTheme({});

const ColumnNameText = styled('span')({
  color: 'rgba(0, 0, 0, 1)',
  fontSize: '14px',
  fontWeight: 'bold',
  width: '100%',
  whiteSpace: 'nowrap',
});

const Table = styled(Grid)({
  margin: '16px 0px',
  paddingBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    overflow: 'auto',
  },
});

const Row = styled(Grid)({
  marginBottom: '15px',
  [theme.breakpoints.only('xs')]: {
    marginBottom: '60px !important',
  },
});

const RowItem = styled(Grid)({
  minWidth: '200px',
});



let tableUpdateCount = 0;
const MAX_TABLE_UPDATES = 10;

const resetTableCounter = () => {
  setTimeout(() => {
    tableUpdateCount = 0;
  }, 100);
};

function TableInput(props) {
  const {
    data,
    getDataFromComponent,
    tableErrors,
    setRequiredTableErrors,
  } = props;

  const [columns, setColumns] = useState(null);
  const [numRows, setNumRows] = useState(null);
  const [exportType, setExportType] = useState(null);

  // Get the table answer keys
  const [tableAnswerKeys, setTableAnswerKeys] = useState(
    getTableAnswerKeys(data)
  );

  const [tableAnswers, setTableAnswers] = useState(null);

  // Initial on load use effect
  useEffect(() => {
    // Set data structure
    setColumns(data.data);
    setNumRows(parseInt(data.numRows));
    setExportType(data.exportType);

    let answerData = props.form.answer_data[data.key];

    // If there already exists answer data,
    // Try to set it into the state
    // Else create an empty table
    if (answerData) {
      try {
        let parsedAnswerData = parseAnswerData(
          answerData,
          data.data,
          data.exportType,
          data.rowLabels
        );
        if (parsedAnswerData == null) throw 'Error loading answers';
        setTableAnswers(parsedAnswerData);
      } catch (e) {
        // If there is an error loading the table answers from the form session
        let noAnswerTable = emptyTableWithDatetime(data);
        updateStateAndTable(noAnswerTable);
      }
    } else {
      // This is a new table with no answerData
      let noAnswerTable = emptyTableWithDatetime(data);
      updateStateAndTable(noAnswerTable);
    }
  }, []);

  // Use effect for handling if the table structure changes
  useEffect(() => {
    if (++tableUpdateCount > MAX_TABLE_UPDATES) {
      console.error(
        'Potential infinite loop detected in TableInput useEffect, preventing update'
      );
      resetTableCounter();
      return;
    }

    let answerKeys = getTableAnswerKeys(data);

    setTableAnswerKeys(answerKeys);
    // Converts the data into the correct format
    let updatedTable = {
      table_answer: convertTableDataToCorrectFormat(
        exportType,
        tableAnswers,
        columns,
        data.rowLabels
      ),
    };

    if (updatedTable.table_answer != null) {
      // Save the data in the redux state
      getDataFromComponent(
        updatedTable,
        data.key,
        data.question_text,
        data.data,
        null,
        null,
        answerKeys
      );
      resetTableCounter();
    }
  }, [data.data, data.exportType, data.numRows, data.rowLabels]);

  // Updates both the local and global states
  // With the given answer table
  const updateStateAndTable = (givenAnswerTable) => {
    setTableAnswers(givenAnswerTable); // Set to empty table

    let updated_table = {
      table_answer: convertTableDataToCorrectFormat(
        data.exportType,
        givenAnswerTable,
        data.data,
        data.rowLabels
      ),
    };

    getDataFromComponent(
      updated_table,
      data.key,
      data.question_text,
      data.data,
      null,
      null,
      tableAnswerKeys
    );
  };

  // Used to determine if on xs small or not
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  });

  // Updates a value in the table of answers
  const handleUpdateValue = (rIdx, cIdx, value, isCalc) => {
    let newTable = [...tableAnswers];

    if (tableAnswers[rIdx][cIdx].value != null) {
      newTable[rIdx][cIdx].value.calculatedValue = value;

      // If it isn't a calc refresh, overwrite the default Value
      if (!isCalc) newTable[rIdx][cIdx].value.defaultValue = value;
    } else {
      newTable[rIdx][cIdx].calculatedValue = value;

      // If it isn't a calc refresh, overwrite the default Value
      if (!isCalc) newTable[rIdx][cIdx].defaultValue = value;
    }

    updateStateAndTable(newTable);

    let newTableErrors = { ...tableErrors };
    delete newTableErrors[data.key];
    setRequiredTableErrors(newTableErrors);
  };

  // Render the valid input type
  const renderInputType = (col, rowIdx, colIdx) => {
    let dataObject = {
      answers: tableAnswers,
      column: col,
      rowIndex: rowIdx,
      columnIndex: colIdx,
      updateValue: handleUpdateValue,
      isReadOnly:
        data.read_only || isCellReadOnly(data.columns ?? data.tableCells, col.id, rowIdx),
      tableCells: data.columns ?? data.tableCells,
      size: 'normal',
    };

    switch (col.type) {
      case 'text':
        return <TextInput data={dataObject} />;
      case 'datetime':
        return <DatetimeInput data={dataObject} />;
      case 'number':
        return <NumberInput data={dataObject} />;
      case 'dropdown':
        return <DropdownInput data={dataObject} />;
      default:
        return 'Not a valid type';
    }
  };

  // Render each column
  const renderColumn = (rowIndex) => {
    return (
      <Row
        item
        container
        style={{ gap: isMobile ? '10px' : '15px' }}
        wrap={!isMobile ? 'nowrap' : 'wrap'}
        key={rowIndex}
        xs={12}
      >
        {columns.map((col, columnIndex) => {
          return (
            <RowItem
              item
              xs={12}
              key={rowIndex.toString() + columnIndex.toString()}
            >
              <Grid item xs={12}>
                {isMobile ? (
                  <ColumnNameText id={`${data.key}-column-${columnIndex}`}>
                    {col.columnName != ''
                      ? `${col.columnName} ${col.required ? '*' : ''}`
                      : 'Untitled'}
                  </ColumnNameText>
                ) : (
                  rowIndex === 0 && (
                    <ColumnNameText id={`${data.key}-column-${columnIndex}`}>
                      {col.columnName != ''
                        ? `${col.columnName} ${col.required ? '*' : ''}`
                        : 'Untitled'}
                    </ColumnNameText>
                  )
                )}
              </Grid>
              {!isCellDisabled(data.columns ?? data.tableCells, col.id, rowIndex) && (
                <Grid item xs={12}>
                  {renderInputType(col, rowIndex, columnIndex)}
                </Grid>
              )}
            </RowItem>
          );
        })}
      </Row>
    );
  };

  try {
    return (
      columns &&
      numRows &&
      exportType &&
      tableAnswers && (
        <Table container>
          {[...Array(numRows)].map((e, i) => renderColumn(i))}
        </Table>
      )
    );
  } catch (e) {
    return <h5>Error encountered</h5>;
  }
}

const mapStateToProps = (state, props) => {
  return {
    form: state.form,
  };
};

const mapDispatchToProps = {};

TableInput.propTypes = {
  data: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(TableInput);
